import { Link } from 'react-router-dom';
import { Product } from '../types';

interface ProductCardProps {
  product: Product;
  compact?: boolean;
}

export function ProductCard({ product, compact = false }: ProductCardProps) {
  const getStatusBadge = (status: Product['status']) => {
    switch (status) {
      case 'Available':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
            Available
          </span>
        );
      case 'In development':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#0D66E4]/10 text-[#0D66E4] border border-[#0D66E4]/20">
            In development
          </span>
        );
      case 'Planned':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#F6F8FB] text-[#667085] border border-[#E4E7EC]">
            Planned
          </span>
        );
    }
  };

  return (
    <article className="double-bezel h-full flex flex-col group">
      <div className="double-bezel-inner p-6 sm:p-8 flex-1 flex flex-col justify-between">
        <div>
          {/* Header Row: Category & Status */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <span className="text-[11px] font-mono uppercase tracking-wider font-semibold text-[#667085]">
              {product.category}
            </span>
            {getStatusBadge(product.status)}
          </div>

          {/* Product Name */}
          <h3 className="text-xl sm:text-2xl font-bold tracking-tight text-[#101828] mb-2 group-hover:text-[#0D66E4] transition-colors">
            {product.name}
          </h3>

          <p className="text-sm font-medium text-[#667085] mb-4">
            {product.tagline}
          </p>

          <p className="text-sm text-[#475467] leading-relaxed mb-6">
            {compact ? product.shortDescription : product.detailedDescription}
          </p>

          {!compact && (
            <div className="space-y-3 pt-4 border-t border-[#E4E7EC]/70 text-xs mb-6">
              <div>
                <span className="font-semibold text-[#101828]">Target Segment: </span>
                <span className="text-[#667085]">{product.targetAudience}</span>
              </div>
              <div>
                <span className="font-semibold text-[#101828]">Architecture: </span>
                <span className="text-[#667085]">{product.architecturalNotes}</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer: Status Explanation & Action */}
        <div className="pt-4 border-t border-[#E4E7EC]/70">
          <p className="text-xs text-[#667085] italic mb-4">
            {product.statusNote}
          </p>

          <div className="flex items-center justify-between gap-3">
            {product.liveUrl ? (
              <a
                href={product.liveUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-xs font-semibold text-[#0D66E4] hover:text-[#0B56C2] group/link transition-colors"
              >
                <span>{product.ctaLabel || 'Visit website'}</span>
                <svg
                  className="w-3.5 h-3.5 text-[#0D66E4] transition-transform duration-200 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                </svg>
              </a>
            ) : (
              <Link
                to={`/contact?subject=${encodeURIComponent(product.name)}`}
                className="inline-flex items-center gap-2 text-xs font-semibold text-[#101828] hover:text-[#0D66E4] group/link transition-colors"
              >
                <span>{product.ctaLabel || 'Inquire regarding roadmap'}</span>
                <svg
                  className="w-3.5 h-3.5 text-[#0D66E4] transition-transform duration-200 group-hover/link:translate-x-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </Link>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}
